import { getSizesOptions, getComponentTab } from "./utils";
import {
  filterSpecRow,
  updateDerivedFields,
  getFamilyTableOptions,
} from "./PartLib/utils";

// Builds the same expanded catalog the Spec Library tab builds, but as a plain
// flat array instead of per-tab buckets. Raw piping_spec_<Spec> rows store Size
// as a *range* ("1/2 -- 4"); this expands each range into one row per discrete
// size and fills in the derived columns (Material Description, Family Table, …).
//
// The result is stored in PipingContext as `specLibraryTable` and drives the
// cascading Spec → Components → Size → Schedule/Rating → Ends → Material
// dropdowns in the Estimation tab.

const TAB_CATEGORY = { Valves: "Valve", Instruments: "Instrument" };

function categoryTypeForTab(tabName) {
  return TAB_CATEGORY[tabName] || "Bulk";
}

// Which Parts Library tab a spec row belongs to. The raw spec rows carry
// Parts_Lib_Tab directly, so read it off the row and only fall back to the
// (per-component, async) DB lookup when the column is missing.
async function resolveTabName(row, cache) {
  const direct = String(
    row?.Parts_Lib_Tab ?? row?.["Parts Lib Tab"] ?? "",
  ).trim();
  if (direct) return direct;

  const key = `${row?.Spec} ${row?.Components}`;
  if (cache.has(key)) return cache.get(key);

  let tabName = "";
  try {
    tabName = (await getComponentTab(row?.Components, row?.Spec)) || "";
  } catch (error) {
    console.warn("[specLibraryTable] getComponentTab failed:", error);
    tabName = "";
  }

  cache.set(key, tabName);
  return tabName;
}

export async function buildSpecLibraryTable(selectedSpecRows, fullSpecTable) {
  if (!Array.isArray(selectedSpecRows) || selectedSpecRows.length === 0) {
    return [];
  }

  const tabCache = new Map();

  // Group the raw spec rows by Spec + Components so sizes are expanded once per
  // component instead of once per row.
  const groups = new Map();
  for (const row of selectedSpecRows) {
    if (!row?.Spec || !row?.Components) continue;

    const tabName = await resolveTabName(row, tabCache);
    const key = `${row.Spec} ${row.Components}`;

    if (!groups.has(key)) {
      groups.set(key, {
        spec: row.Spec,
        component: row.Components,
        tabName,
        rows: [],
      });
    }

    const group = groups.get(key);
    if (!group.tabName && tabName) group.tabName = tabName;
    group.rows.push(row);
  }

  const out = [];

  for (const { spec, component, tabName, rows } of groups.values()) {
    let sizes = [];
    try {
      sizes = await getSizesOptions(selectedSpecRows, spec, component);
    } catch (error) {
      console.warn(
        `[specLibraryTable] Size expansion failed for ${spec} / ${component}:`,
        error,
      );
      sizes = [];
    }

    const categoryType = categoryTypeForTab(tabName);

    for (const baseRow of rows) {
      const expanded = sizes.filter((s) => s.range === baseRow.Size);

      // A range that can't be expanded (missing piping_ranges_<Spec> entry,
      // unparseable bounds, …) keeps its raw Size instead of dropping the row —
      // one bad range must never empty the dropdowns for a whole spec.
      const sizeValues = expanded.length
        ? expanded.map((s) => s.value)
        : [baseRow.Size].filter(Boolean);

      for (const sizeValue of sizeValues) {
        const newRow = {
          ...filterSpecRow(baseRow),
          select: false,
          Size: sizeValue,
          OriginalSizeRange: baseRow.Size,
          TabName: tabName,
          CategoryType: categoryType,
        };

        try {
          await updateDerivedFields(
            selectedSpecRows,
            (row, key, value) => (row[key] = value),
            newRow,
            { ...newRow },
          );
        } catch (error) {
          console.warn("[specLibraryTable] updateDerivedFields failed:", error);
        }

        try {
          const familyOptions = await getFamilyTableOptions(
            newRow.Components,
            newRow.Spec,
            {
              "Schedule/Rating": newRow["Schedule/Rating"],
              Ends: newRow["Ends"],
              Material: newRow["Material"],
              "Additional Notes": newRow["Additional Notes"],
            },
            fullSpecTable,
          );

          const uniqueNames = Array.from(
            new Set(
              (familyOptions || [])
                .map((cur) => String(cur?.Family_Name ?? "").trim())
                .filter((n) => n && n !== "-"),
            ),
          );

          if (uniqueNames.length === 1 && !newRow["Family Table"]) {
            const onlyName = uniqueNames[0];
            newRow["Family Table"] = onlyName;

            const tableTarget =
              familyOptions.find((cur) => cur.Family_Name === onlyName)
                ?.Family_Table || "";
            if (tableTarget) newRow.FamilyTableTarget = tableTarget;
          }
        } catch (error) {
          console.warn(
            "[specLibraryTable] getFamilyTableOptions failed:",
            error,
          );
        }

        out.push(newRow);
      }
    }
  }

  return out;
}
